import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Eye, EyeSlash, CaretRight, Check, CreditCard, Lock, Shield, X, Clock, ArrowRight } from "@phosphor-icons/react";
import GcrLogo from "../components/GcrLogo";
import apiClient from "../lib/api";

// ── Modal de Confianza Pre-Checkout ───────────────────────────────────────────
function TrustModal({ onConfirm, onCancel, onNoCard, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}>
      <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden"
        style={{ background: "#0D0D12", border: "1px solid rgba(212,175,55,0.2)" }}>

        {/* Franja dorada superior */}
        <div style={{ height: 3, background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />

        <div className="px-6 pt-6 pb-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.2)" }}>
              <Shield weight="fill" className="w-6 h-6" style={{ color: "#D4AF37" }} />
            </div>
            <button onClick={onCancel} className="text-zinc-600 hover:text-zinc-400 transition mt-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          <h2 className="text-2xl font-black text-white mb-1">No te cobramos hoy.</h2>
          <p className="text-zinc-400 text-sm mb-7">Tu tarjeta solo se guarda como garantía. Exactamente igual que cuando rentas un carro.</p>

          {/* Timeline visual */}
          <div className="space-y-0 mb-7">
            <div className="flex gap-4 items-start">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-black text-sm"
                  style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)", color: "#10B981" }}>1</div>
                <div className="w-0.5 h-8 mt-1" style={{ background: "rgba(255,255,255,0.06)" }} />
              </div>
              <div className="pt-1 pb-6">
                <p className="text-white font-bold text-sm">Ahora — Guardas tu tarjeta</p>
                <p className="text-zinc-500 text-xs mt-0.5">$0 cobrados. Acceso inmediato al 100% de la plataforma.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-black text-sm"
                  style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.3)", color: "#D4AF37" }}>2</div>
                <div className="w-0.5 h-8 mt-1" style={{ background: "rgba(255,255,255,0.06)" }} />
              </div>
              <div className="pt-1 pb-6">
                <p className="text-white font-bold text-sm">24 horas — Reloj en marcha</p>
                <p className="text-zinc-500 text-xs mt-0.5">Verás un contador en tu dashboard. Puedes cancelar con un click.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-black text-sm"
                  style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444" }}>3</div>
              </div>
              <div className="pt-1">
                <p className="text-white font-bold text-sm">Solo si decides quedarte — Se cobra</p>
                <p className="text-zinc-500 text-xs mt-0.5">Si no cancelas en 24h, se activa tu suscripción de $50/semana.</p>
              </div>
            </div>
          </div>

          {/* Badges de seguridad */}
          <div className="flex items-center gap-3 mb-7 p-3 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <Lock className="w-4 h-4 flex-shrink-0" style={{ color: "#D4AF37" }} />
            <div>
              <p className="text-xs font-bold text-white">Pago 100% seguro — procesado por Stripe</p>
              <p className="text-[11px] text-zinc-500">El mismo sistema que usa Netflix, Amazon y Spotify.</p>
            </div>
          </div>

          {/* Botón principal */}
          <button onClick={onConfirm} disabled={loading}
            className="w-full py-4 rounded-2xl font-black text-sm tracking-widest uppercase transition flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #D4AF37, #B8962E)", color: "#000" }}
            data-testid="trust-modal-confirm">
            {loading ? (
              <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <><ArrowRight weight="bold" className="w-4 h-4" /> Entendido — continuar al pago seguro</>
            )}
          </button>
          {onNoCard && (
            <button onClick={onNoCard} disabled={loading}
              data-testid="trust-modal-no-card"
              className="w-full mt-3 py-3.5 rounded-2xl text-sm font-bold transition border flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", color: "#d1d5db" }}>
              <Clock className="w-4 h-4" />
              No tengo tarjeta — Quiero 1 día gratis sin tarjeta
            </button>
          )}
          <button onClick={onCancel} disabled={loading}
            className="w-full mt-2 py-2.5 text-xs text-zinc-600 hover:text-zinc-400 transition font-semibold"
            data-testid="trust-modal-cancel">
            Volver al registro
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Register() {
  const { register, loginWithData, user } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requireCard, setRequireCard] = useState(false);
  const [showTrustModal, setShowTrustModal] = useState(false);
  // Ref to suppress the auto-redirect to /plans when we're navigating to /jugadas
  const [redirectTarget, setRedirectTarget] = useState(null);

  // Redirect logged-in users — but respect explicit redirectTarget set by no-card flow
  useEffect(() => {
    if (user && !loading && !redirectTarget) nav("/plans", { replace: true });
    if (user && redirectTarget) nav(redirectTarget, { replace: true });
  }, [user, nav, loading, redirectTarget]);

  // Fetch public flag to know whether trial requires card
  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get("/public/signup-config");
        setRequireCard(!!r.data?.require_card_for_trial);
      } catch { /* default false */ }
    })();
  }, []);

  // Actually call the Stripe checkout API
  const proceedToCheckout = async () => {
    setLoading(true);
    try {
      const r = await apiClient.post("/auth/register-with-trial-checkout", {
        email, name, password,
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.error("No se pudo iniciar el checkout. Intenta de nuevo.");
      setShowTrustModal(false);
    } catch (err) {
      setShowTrustModal(false);
      if (err?.response?.status === 429) {
        toast.error("Demasiados intentos. Espera unos minutos.");
      } else {
        toast.error(err?.response?.data?.detail || "Error al registrar. Por favor intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  // No-card free day flow
  const proceedNoCard = async () => {
    setLoading(true);
    try {
      const r = await apiClient.post("/auth/request-no-card-trial", {
        email,
        name,
        password,
        origin_url: window.location.origin,
      });
      if (r.data?.token && r.data?.user) {
        setRedirectTarget("/jugadas");
        loginWithData(r.data.token, r.data.user);
      }
      toast.success("¡Acceso de 1 día activado! Tienes 24 horas de acceso completo.");
    } catch (err) {
      setShowTrustModal(false);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? "Error al procesar el registro. Verifica tus datos."
          : "Error al activar el acceso gratuito.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (requireCard) {
      // Show trust modal first — don't go to Stripe yet
      setShowTrustModal(true);
      return;
    }
    setLoading(true);
    try {
      await register(email, name, password);
      toast.success("Cuenta creada. Bienvenido.");
      nav("/jugadas", { replace: true });
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Demasiados intentos. Espera unos minutos antes de intentarlo de nuevo.");
      } else if (err?.response?.status === 400) {
        toast.error(err.response.data?.detail || "Email ya registrado. Intenta iniciar sesión.");
      } else {
        toast.error(err?.response?.data?.detail || "Error al registrar. Por favor intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 text-white" style={{ background: "#050508" }}>
      {/* Trust Modal */}
      {showTrustModal && (
        <TrustModal
          onConfirm={proceedToCheckout}
          onCancel={() => setShowTrustModal(false)}
          onNoCard={proceedNoCard}
          loading={loading}
        />
      )}
      <div className="flex flex-col justify-center px-8 lg:px-16 py-10 relative" style={{ background: "#050508" }}>
        <Link to="/" className="absolute top-8 left-8" data-testid="register-back-home">
          <GcrLogo size={56} />
        </Link>
        <div className="max-w-sm w-full mx-auto">
          <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-3" style={{ color: "#D4AF37" }}>
            Crear cuenta
          </div>
          <h1 className="font-sport font-black leading-[1.0] mb-4" style={{ fontSize: "clamp(2.4rem, 6vw, 3.5rem)", letterSpacing: "-0.03em" }}>
            Empieza<br />gratis
          </h1>
          <p className="text-sm mb-8" style={{ color: "#52525B" }}>
            {requireCard
              ? "1 día gratis. Tarjeta requerida — cancela cuando quieras."
              : "1 día gratis. Sin tarjeta de crédito."}
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase font-black mb-2" style={{ color: "#9C9CAA" }}>Nombre</label>
              <input data-testid="register-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required className="input-field" placeholder="Tu nombre" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase font-black mb-2" style={{ color: "#9C9CAA" }}>Email</label>
              <input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="input-field" placeholder="tu@email.com" />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.2em] uppercase font-black mb-2" style={{ color: "#9C9CAA" }}>Contraseña</label>
              <div className="relative">
                <input data-testid="register-password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="input-field pr-10" placeholder="Mínimo 6 caracteres" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#3F3F46" }}>
                  {show ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <ul className="space-y-1.5 pt-1">
              {(requireCard
                ? [
                    "1 día gratis con tarjeta",
                    "Después: $50/semana automático",
                    "Cancela cuando quieras desde tu perfil",
                  ]
                : [
                    "Acceso al dashboard premium",
                    "Jugadas en tiempo real",
                    "Sin tarjeta para empezar",
                  ]
              ).map((f) => (
                <li key={f} className="text-xs flex items-center gap-2" style={{ color: "#52525B" }}>
                  <Check weight="bold" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#D4AF37" }} /> {f}
                </li>
              ))}
            </ul>
            <button type="submit" disabled={loading} className="btn-gold w-full mt-6 py-4 text-sm tracking-widest uppercase font-black" data-testid="register-submit">
              {loading
                ? (requireCard ? "Redirigiendo a pago..." : "Creando...")
                : requireCard
                  ? <>Continuar con tarjeta <CreditCard weight="bold" className="w-4 h-4 inline" /></>
                  : <>Crear cuenta <CaretRight weight="bold" className="w-4 h-4 inline" /></>
              }
            </button>

            {requireCard && (
              <p className="mt-3 text-[11px] text-center leading-relaxed" style={{ color: "#52525B" }}
                 data-testid="register-no-card-hint">
                ¿No tienes tarjeta? Escríbenos a{" "}
                <a href="mailto:soporte@ganasconronald.com"
                   className="font-semibold underline-offset-2 hover:underline"
                   style={{ color: "#D4AF37" }}
                   data-testid="register-support-email">
                  soporte@ganasconronald.com
                </a>
                {" "}y te ayudamos con otra forma de pago.
              </p>
            )}
          </form>

          <div className="mt-6 text-xs text-center" style={{ color: "#71717A" }}>
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="font-semibold transition-colors" style={{ color: "#D4AF37" }}
              data-testid="register-go-login">
              Inicia sesión
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden" style={{ borderLeft: "1px solid #0E0E14" }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.pexels.com/photos/15828942/pexels-photo-15828942.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)', opacity: 0.3 }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(5,5,8,0.95) 0%, rgba(5,5,8,0.7) 50%, rgba(5,5,8,0.9) 100%)" }} />
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(ellipse at 70% 30%, rgba(212,175,55,0.1) 0%, transparent 60%)",
        }} />
        <div className="relative h-full flex items-center justify-start p-16">
          <div className="max-w-md">
            <div style={{ height: 1, background: "linear-gradient(90deg, #D4AF37, transparent)", marginBottom: 24, width: 48 }} />
            <div className="font-sport font-black leading-tight mb-6" style={{ fontSize: "clamp(1.4rem, 3vw, 2rem)", letterSpacing: "-0.02em" }}>
              Tu acceso a signals de élite —{" "}
              <span className="text-gradient-gold">análisis real,</span>{" "}
              sin humo.
            </div>
            {/* Mini stats */}
            <div className="flex gap-6 mt-8">
              {[
                { n: "92%", l: "Win Rate" },
                { n: "1", l: "Día gratis" },
                { n: "24/7", l: "Soporte" },
              ].map(({ n, l }) => (
                <div key={l}>
                  <div className="font-mono font-black text-2xl text-gradient-gold">{n}</div>
                  <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: "#3F3F46" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
